import type { SessionState } from "../../hooks/types";

import { IS_MOCKED_SESSION } from "@features/session/constants";
import { useAppStore } from "@hooks/useAppStore";
import type { DailyProgressState } from "@hooks/useDailyProgressStore";
import { getCurrentDateKey } from "@memorang/helpers/date";
import { hasStaticFeedback } from "@memorang/helpers/feedback";
import { isAnswered } from "@memorang/helpers/item";
import { resolveMaxScore } from "@memorang/helpers/scoring/max-score";
import { calcScore } from "@memorang/helpers/scoring/multichoice";
import { isAiSubmittable, isNonAiItemType } from "@memorang/helpers/session";
import { isHttpUrl } from "@memorang/helpers/url";
import { getSubmittedRawInput } from "@memorang/helpers/user-submitted-answer";
import type {
	AIFeedbackResponse,
	AIResponsePayload,
	Grading,
	SubmittedRawInput,
} from "@memorang/types";
import type { Confidence } from "@memorang/types";
import type { TrackAnswerEventRequest } from "../../api/types";
import {
	isPteAssessorAgentItem,
	isPteAssessorSpeakingItem,
} from "../../constants/pte/assessor";
import { isPteFeedbackAgentItem } from "../../constants/pte/feedback";
import {
	getBlankValuesResponse,
	getNormalizedScore,
} from "../../helpers/ai-feedback";
import {
	getDictationAnswerKeyWords,
	toNormalizedDictationScore,
} from "../../helpers/dictation";
import { isIntroScreen } from "../../helpers/intro-screens";
import { deriveItemTimingPlan } from "../../helpers/item-timing-plan";
import {
	isBlankDropdown,
	isMatchingPair,
} from "../../helpers/item-type-guards";
import { highlightWordChoices } from "../../helpers/original-question";
import { getEarnedPoints } from "../../helpers/points";
import {
	buildFeedbackAiResponse,
	hasCapturedFeedbackResponse,
	resolveFeedbackPoints,
} from "../../helpers/pte-feedback-response";
import {
	getResolvedCorrectAnswerIds,
	withItemViewedFallback,
} from "../../helpers/session";
import {
	hasCapturedResponse,
	shouldUseSummativeAiAssessment,
	shouldUseSummativeDynamicFeedback,
} from "../../helpers/summative-ai-gate";
import { getTimeOnItemSeconds } from "../../helpers/time-on-item";
import useAIFeedbackStore from "../../hooks/useAIFeedbackStore";
import useResponseControlsStore from "../../hooks/useResponseControlsStore";
import useSummativeAssessmentStore from "../../hooks/useSummativeAssessmentStore";
import {
	getAnsweredFormStates,
	getIsCorrectAnswer,
} from "./answer-correctness";
import {
	buildCurrentAnswer,
	buildTrackAnswerEventRequest,
} from "./answer-submission";

// Fallback floor when the scoring response omits minPoints. Negative
// marking (e.g. MCMAR answer keys with -1 per wrong selection) can push the
// summed points below zero, but the reported minimum is 0.
const MIN_EARNED_POINTS = 0;

const getScoringMinPoints = (scoring: Grading | undefined): number =>
	scoring && "minPoints" in scoring && scoring.minPoints != null
		? scoring.minPoints
		: MIN_EARNED_POINTS;

const calculateAndUpdateProgress = (state: SessionState, forward?: boolean) => {
	const {
		updateProgress,
		currentProgress,
		numGoal,
		isSummativeTest,
		blockStates,
		currentBlockIndex,
		sessionItems,
	} = state;

	const finalProgress = isSummativeTest
		? blockStates?.get(currentBlockIndex)?.currentProgress || 0
		: currentProgress;

	const blockItems = sessionItems?.[currentBlockIndex] ?? [];
	const denominator = isSummativeTest
		? blockItems.filter((item) => !isIntroScreen(item)).length ||
			blockItems.length ||
			numGoal!
		: numGoal!;

	const normalizedProgress =
		denominator > 0
			? Math.round(finalProgress * denominator) / denominator
			: finalProgress;

	let nextProgress = 0;

	if (forward) {
		const newProgress = Math.min(normalizedProgress + 1 / denominator, 1);
		nextProgress = newProgress;
		updateProgress(newProgress);
	} else {
		const newProgress = Math.max(normalizedProgress - 1 / denominator, 0);
		updateProgress(newProgress);
	}
	return nextProgress;
};

const handlePostAnswer = (
	{
		state,
		startTime,
		confidence,
		dailyProgressState,
		projectId,
		organizationId,
		examId,
		// submitAnswer = true,
		aiFeedback,
		totalScore,
		slideshowDurationInSeconds,
		currentItemIndex,
		showButtonNavSkip,
		shouldCreateAnswerForAiItem,
		mutateTrackAnswerEvent,
		userResponseText,
	}: {
		state: SessionState;
		startTime: Date;
		confidence?: Confidence;
		dailyProgressState?: DailyProgressState;
		projectId: number;
		organizationId: number;
		examId?: string;
		submitAnswer?: boolean;
		aiFeedback?: AIFeedbackResponse;
		totalScore?: number;
		slideshowDurationInSeconds?: number;
		currentItemIndex: number;
		showButtonNavSkip?: boolean;
		shouldCreateAnswerForAiItem?: boolean;
		mutateTrackAnswerEvent?: (data: {
			answerEventPayload: TrackAnswerEventRequest;
		}) => void;
		action?: string;
		userResponseText?: string;
	},
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
) => {
	const {
		sessionItems,
		itemStates,
		id: sessionId,
		updateAnswers,
		updateItemStates,
		updateAnswerEventInProgress,
		isRecommendedTask,
		updateItemAttempts,
		updateShowConfetti,
		currentBlockIndex,
		isSummativeTest,
		blockStates,
		collectionId,
		formStates,
		timeElapsed,
		sessionTime,
		itemTimeElapsed,
		sectionElapsedSeconds,
		itemViewedTimestamps,
	} = state;

	const isImageChallengeApp = useAppStore.getState().app.isImageChallengeApp;

	const dailyProgressByData = dailyProgressState?.dailyProgressByData;
	const setDailyProgressByDate = dailyProgressState?.setDailyProgressByDate;

	const key = `${examId}-${getCurrentDateKey()}`;

	const currentDailyProgress = dailyProgressByData?.[key];

	const currentItem = sessionItems[currentBlockIndex][currentItemIndex];
	const { id: currentItemId } = currentItem;

	// Use saved timeTaken for AI feedback; avoids including AI streaming delay.
	const storedTimeTaken = aiFeedback
		? state.answers?.get(currentItemId!)?.timeTaken
		: undefined;
	// Audio stimulus playback and the post-audio mic delay are not answering
	// time; count from when the item became answerable, mirroring the visible
	// item timers.
	const audioCompletedAt =
		useResponseControlsStore.getState().audioCompletedItems[currentItemId!];
	const { micDelaySecondsAfterAudio } = deriveItemTimingPlan({
		item: currentItem,
		isSummativeTest,
		inReviewMode: state.inReviewMode,
	});
	const answerableAt = audioCompletedAt
		? audioCompletedAt + micDelaySecondsAfterAudio * 1000
		: 0;
	const effectiveStartTime = Math.max(startTime.getTime(), answerableAt);
	const timeTaken =
		storedTimeTaken ??
		Math.min(
			Math.max(0, Math.round((Date.now() - effectiveStartTime) / 1000)),
			300,
		);
	const response = currentItem.response;

	const itemPoints = response?.itemPoints ?? 0;
	const totalPoints = response?.totalPoints ?? 0;

	const choices =
		response.type === "choice" || response.type === "OPTION_SELECTION"
			? response.choices
			: [];

	const currentItemState = itemStates?.get(currentItemId!);

	const answerKeyChoices =
		response.type === "INLINE_TEXT_SELECTION"
			? highlightWordChoices(response.incorrectWords)
			: (choices ?? []);

	const resolvedCorrectAnswerIds = getResolvedCorrectAnswerIds(
		answerKeyChoices,
		currentItem.scoring,
	);
	const answerId = resolvedCorrectAnswerIds[0];

	const numDistractors = choices ? choices.length - 1 : 0;

	const selectedChoiceIds =
		currentItemState?.selectedChoiceIds ||
		currentItem.answer?.selectedChoiceIds ||
		[];

	const selectedChoiceItemId = selectedChoiceIds?.[0];

	const isNoteCompletion = response?.type === "form";

	const currentFormStates = (() => {
		if (isNoteCompletion && response.fields?.length === 1) {
			const textValue =
				currentItemState?.selectedChoiceIds?.[0] ||
				currentItem.answer?.selectedChoiceIds?.[0];
			if (textValue) {
				return { [response.fields[0].id]: textValue };
			}
		}
		return formStates?.[currentItemId!];
	})();

	const itemType = currentItem.metadata?.type;

	const userResponseMap = useAIFeedbackStore.getState().userResponse;
	const hasLiveBlankResponse = userResponseMap?.[currentItemId!] !== undefined;

	const selectedBlankValues =
		response.type === "TOKEN_MAPPING" ||
		response.type === "INLINE_OPTION_SELECTION" ||
		(response.type === "SHORT_TEXT_ENTRY" &&
			(itemType === "FIBTIL" || itemType === "FIBTIL_CORE"))
			? hasLiveBlankResponse
				? getBlankValuesResponse(userResponseMap, currentItemId!)
				: currentItem.answer?.selectedBlankValues
			: undefined;

	const selectedOrder = (() => {
		if (response.type !== "ORDER_RECONSTRUCTION") {
			return undefined;
		}
		const raw = useAIFeedbackStore.getState().userResponse?.[currentItemId!];
		return Array.isArray(raw)
			? raw.map(String)
			: currentItem.answer?.selectedOrder;
	})();

	// For assessor items, grade by awarded score since there’s no answer key.
	const isCorrect = isPteAssessorAgentItem(itemType)
		? (aiFeedback?.displayScore ?? aiFeedback?.score ?? 0) > 0
		: getIsCorrectAnswer({
				currentItem,
				currentItemState,
				currentFormStates,
				choices,
				selectedChoiceIds,
				selectedChoiceItemId,
				answerId,
				resolvedCorrectAnswerIds,
				selectedBlankValues,
				selectedOrder,
				userResponseText,
			});

	const hints = currentItemState?.hints?.length
		? currentItemState?.hints
		: null;
	const currentBlockState = blockStates?.get(currentBlockIndex);
	const markedItemsForReview = currentBlockState?.markedItemsForReview;
	const highlightedItems = currentBlockState?.highlightedItems;

	// Feedback-agent items (FIBDDR/FIBDPR/ROPR/etc.) submit through a plain
	// button with no confidence picker — the caller hardcodes confidence=50,
	// which would otherwise cap calcScore's confidence-weighted result to the
	// 50-84 band regardless of correctness. Score flat like summative instead.
	const { score } =
		isSummativeTest || isPteFeedbackAgentItem(itemType)
			? { score: isCorrect ? 100 : 0 }
			: calcScore(
					confidence ?? 0,
					Boolean(isCorrect),
					timeTaken,
					slideshowDurationInSeconds ?? 0,
					currentItem,
				);

	if (currentDailyProgress && isRecommendedTask && score > 90) {
		const { numCompleted, numGoal } = currentDailyProgress;
		const updatedProgress = {
			[key]: {
				numCompleted: (numCompleted || 0) + 1,
				numGoal,
				status: "ACTIVE" as const,
			},
		};
		setDailyProgressByDate?.(updatedProgress);
	}

	const isAiSubmittableResponse = isAiSubmittable(
		response.type,
		itemType,
		currentItem.metadata?.toeic_artifact_type,
		hasStaticFeedback(currentItem.feedback),
	);
	const useAiScoring =
		isAiSubmittableResponse && !isPteFeedbackAgentItem(itemType);
	const answeredFormStates = getAnsweredFormStates(
		response.type === "form" ? response.fields || [] : [],
		currentFormStates,
	);

	const getScoringChoices = () => {
		if (choices?.length) {
			return choices;
		}
		if (response.type === "INLINE_TEXT_SELECTION") {
			return answerKeyChoices;
		}
		if (isBlankDropdown(currentItem) && "blanks" in response) {
			return response.blanks.flatMap((b) => b.choices);
		}
		return [];
	};

	const getScoringSelectedChoiceIds = () => {
		if (isBlankDropdown(currentItem) && currentFormStates) {
			return Object.values(currentFormStates).filter(Boolean);
		}
		return selectedChoiceIds;
	};

	const rawEarnedPoints = getEarnedPoints({
		itemType,
		responseType: response.type,
		scoring: currentItem.scoring,
		choices: getScoringChoices(),
		selectedChoiceIds: getScoringSelectedChoiceIds(),
		selectedOrder,
		isCorrect,
		totalPoints,
		itemPoints,
		answeredFormStates,
		aiFeedback,
		blankFormState:
			selectedBlankValues ??
			(response.type === "TOKEN_MAPPING" ||
			response.type === "INLINE_OPTION_SELECTION" ||
			isBlankDropdown(currentItem) ||
			isMatchingPair(currentItem)
				? currentFormStates
				: undefined),
		toeicArtifactType: currentItem.metadata?.toeic_artifact_type,
		hasFeedback: hasStaticFeedback(currentItem.feedback),
		userResponseText,
	});

	const earnedPoints =
		rawEarnedPoints !== undefined
			? Math.max(getScoringMinPoints(currentItem.scoring), rawEarnedPoints)
			: undefined;

	const currentAnswer = buildCurrentAnswer({
		timeTaken,
		confidence,
		isCorrect,
		score,
		selectedChoiceIds,
		answeredFormStates,
		earnedPoints,
		selectedBlankValues,
		selectedOrder,
		userResponseText,
	});

	if (isImageChallengeApp) {
		updateItemAttempts({
			itemId: `${currentItemId}`,
			answered: isCorrect,
			showTryAgain: !isCorrect,
			selectedChoiceId: selectedChoiceItemId,
			attempted: true,
			correct: isCorrect,
		});
		if (isCorrect) {
			updateAnswers(currentItemId!, currentAnswer);
			updateShowConfetti({ itemId: `${currentItemId}`, showConfetti: true });
		}
	} else if (
		!(isSummativeTest || showButtonNavSkip) ||
		(isAiSubmittableResponse && shouldCreateAnswerForAiItem)
	) {
		updateAnswers(currentItemId!, currentAnswer);
		updateItemStates({
			itemId: currentItemId!,
			selectedChoiceId: selectedChoiceItemId,
			correctAnswerIds: resolvedCorrectAnswerIds,
			scores: [score],
			answered: true,
		});
	} else if (showButtonNavSkip) {
		updateAnswers(currentItemId!, {
			timeTaken,
			confidence: confidence ?? 0,
			selectedChoiceIds: [],
		});
	}

	// TODO: Revisit this — is causing lot of issues with stacks
	// if (!submitAnswer) {
	// 	return;
	// }

	// Runner posts these; skipping here avoids a double-post. Skipped items fall through.
	if (
		shouldUseSummativeAiAssessment(
			useAppStore.getState().sessionView?.testDriver,
			isSummativeTest,
			itemType,
		) &&
		hasCapturedResponse(currentItemId!)
	) {
		return;
	}

	updateAnswerEventInProgress(true);

	const aiResponse: AIResponsePayload = {
		feedback: aiFeedback?.explanation ?? "",
		subskillAssessments:
			aiFeedback?.subskillAssessments?.map((subskill) => ({
				key: subskill.key,
				name: subskill.name,
				score: subskill.score,
				...(subskill.displayScore !== undefined
					? { displayScore: subskill.displayScore }
					: {}),
				maxScore: subskill.maxScore ?? totalScore!,
				...(isSummativeTest && subskill.normalizedScore !== undefined
					? { normalizedScore: subskill.normalizedScore }
					: {}),
				...(isSummativeTest && subskill.multiplier !== undefined
					? { multiplier: subskill.multiplier }
					: {}),
			})) ?? [],
		ai_run_id: aiFeedback?.runDetails?.runId ?? "",
		ai_thread_id: aiFeedback?.runDetails?.threadId ?? "",
		metadata: {
			assistantId: aiFeedback?.runDetails?.assistantId ?? "",
		},
		answer: aiFeedback?.answer ?? "",
	};

	const aiNormalizedScore = getNormalizedScore(
		aiFeedback?.score ?? 0,
		totalScore!,
	);

	const sessionEvents = withItemViewedFallback(
		state.sessionEvents?.get(currentItemId!),
	);

	const timeOnItemSeconds =
		getTimeOnItemSeconds(itemViewedTimestamps, currentItemId!) ?? 0;

	const rawInputValue =
		aiFeedback?.answer ||
		useAIFeedbackStore.getState().aiResponse?.[currentItemId!]?.answer ||
		userResponseText ||
		getSubmittedRawInput(currentItem.answer)?.value;
	const isSpeakingInput = isPteAssessorSpeakingItem(itemType);
	// Until the recording is uploaded the store holds a device-local file://
	// or blob: URI, which the backend cannot fetch.
	const isValidRawInput = isSpeakingInput
		? isHttpUrl(rawInputValue)
		: Boolean(rawInputValue);
	const rawInput: SubmittedRawInput | undefined =
		isPteAssessorAgentItem(itemType) && rawInputValue && isValidRawInput
			? {
					inputType: isSpeakingInput ? "audio" : "text",
					value: rawInputValue,
				}
			: undefined;

	const dictationAnswerKeyWords = getDictationAnswerKeyWords(currentItem);
	const isDictationItem = dictationAnswerKeyWords != null;
	// Normalization applies to the outgoing payload only; the local answer and the
	// answers map stay on the raw word-count scale every in-session surface reads.
	const normalizedDictationPoints =
		isSummativeTest && dictationAnswerKeyWords && earnedPoints != null
			? toNormalizedDictationScore(earnedPoints, dictationAnswerKeyWords.length)
			: undefined;

	const trackAnswerEventRequest = buildTrackAnswerEventRequest({
		currentItem,
		currentItemId: currentItemId!,
		itemType,
		response,
		sessionId,
		collectionId,
		projectId,
		organizationId,
		isSummativeTest,
		isCorrect,
		selectedChoiceIds,
		selectedChoiceItemId,
		confidence,
		timeTaken,
		itemTimeElapsed,
		timeOnItemSeconds,
		sectionElapsedSeconds,
		score,
		earnedPoints: normalizedDictationPoints ?? earnedPoints,
		numDistractors,
		useAiScoring,
		aiNormalizedScore,
		normalizedPoints: aiFeedback?.normalizedPoints,
		displayPoints:
			aiFeedback?.displayScore ?? (isDictationItem ? earnedPoints : undefined),
		aiResponse,
		markedItemsForReview,
		highlightedItems,
		hints,
		timeElapsed,
		sessionTime,
		sessionEvents,
		selectedBlankValues,
		selectedOrder,
		userResponseText,
		rawInput,
	});

	const hasFeedbackResponse = hasCapturedFeedbackResponse(currentItem);
	const shouldEnrichSummative =
		hasFeedbackResponse &&
		shouldUseSummativeDynamicFeedback(
			useAppStore.getState().sessionView?.testDriver,
			isSummativeTest,
			itemType,
		);
	const shouldStashFormative =
		hasFeedbackResponse &&
		!isSummativeTest &&
		!IS_MOCKED_SESSION &&
		isPteFeedbackAgentItem(itemType);

	if (shouldEnrichSummative || shouldStashFormative) {
		const feedbackAiResponse = buildFeedbackAiResponse(currentItem);
		const feedbackPoints = resolveFeedbackPoints(
			itemType,
			isCorrect,
			earnedPoints,
			resolveMaxScore(currentItem.scoring),
		);

		if (shouldEnrichSummative) {
			// Mutated, not copied: summative persists the pending blob on this very
			// post, so the enrichment must reach the request sent below.
			trackAnswerEventRequest.aiResponse = feedbackAiResponse;
			trackAnswerEventRequest.performance.points =
				normalizedDictationPoints ?? feedbackPoints;
			useSummativeAssessmentStore
				.getState()
				.stashFeedbackRequest(currentItemId!, trackAnswerEventRequest);
		} else {
			useAIFeedbackStore.getState().stashAnswerEventRequest(currentItemId!, {
				...trackAnswerEventRequest,
				aiResponse: feedbackAiResponse,
				performance: {
					...trackAnswerEventRequest.performance,
					points: feedbackPoints,
				},
			});
		}
	}

	if (IS_MOCKED_SESSION) {
		return;
	}

	mutateTrackAnswerEvent?.({
		answerEventPayload: trackAnswerEventRequest,
	});

	state.clearSessionEvents?.(currentItemId!);
};

type ShowAiSubmitResponseButtonParams = {
	sessionState: Pick<
		SessionState,
		| "answers"
		| "sessionItems"
		| "currentBlockIndex"
		| "isSummativeTest"
		| "inReviewMode"
	>;
	currentItemIndex: number;
	currentUserResponse?: unknown;
	currentAudioResponse?: unknown;
	shouldValidateAudio?: boolean;
};

export const getShowSubmitAnswerAIFeedbackButton = ({
	sessionState,
	currentItemIndex,
	currentUserResponse,
	currentAudioResponse,
	shouldValidateAudio,
}: ShowAiSubmitResponseButtonParams) => {
	const {
		answers,
		sessionItems,
		currentBlockIndex,
		isSummativeTest,
		inReviewMode,
	} = sessionState;
	const currentItem = sessionItems[currentBlockIndex][currentItemIndex];
	const currentAnswer = answers?.get(currentItem.id!);

	const response = currentItem.response;

	const isAiSubmittableResponse = isAiSubmittable(
		response.type,
		currentItem.metadata?.type,
		currentItem.metadata?.toeic_artifact_type,
		hasStaticFeedback(currentItem.feedback),
	);

	const hasRealAnswer = isAnswered(currentAnswer);
	const baseShowCondition = !hasRealAnswer && isAiSubmittableResponse;

	const isNonAiItem = isNonAiItemType(currentItem.metadata?.type);

	const shouldShowSubmitAnswerAIFeedbackButton =
		(response.type === "reorder" && !hasRealAnswer) ||
		(isNonAiItem && !hasRealAnswer && Boolean(currentUserResponse)) ||
		(baseShowCondition &&
			Boolean(currentUserResponse) &&
			(shouldValidateAudio ? Boolean(currentAudioResponse) : true));

	return {
		disabled: hasRealAnswer,
		show:
			shouldShowSubmitAnswerAIFeedbackButton &&
			!isSummativeTest &&
			!inReviewMode,
	};
};

export { calculateAndUpdateProgress, handlePostAnswer };
